/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { isEqual } from "lodash";
import React, { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { usePostRequest } from "../../../../../../api/helper";
import {
    clearCoverageRequirementsState,
} from "../../../../../../redux/ducks/CoverageRequirements/slice";
import { useAppDispatch } from "../../../../../../redux/hooks";
import ArgosExpandableContainer from "../../../../../components/Containers/ArgosExpandableContainer";
import { useIsGlobalDialogOpen } from "../../../../../components/Global/Dialog/CloseGlobalDialogButton";
import {
    coverageRequirementsModifyTreeEndpoint,
    EDatasetExecutionStatus,
    SELECTED_DATASET_ID_URL_PARAM
} from "../../constants";
import LeftPanelCoverageRequirements from "./LeftPanelComponents/LeftPanelCoverageRequirements";
import RightPanelCoverageRequirements from "./RightPanelComponents/RightPanelCoverageRequirements";

interface ICoverageRequirementsProps {
  studyId: string
  studyName: string
  isPlanOfRecord?: boolean
  isReadOnlyStudy?: boolean
  setDatasetChange: any
  datasetChange: any
  fetchStudy:any
}

/**
 * Custom hook to handle deep state changes. This is useful for when we want to track changes to a state object
 * and we want to avoid re-renders.
 * @param initialState
 */
function useDeepState<T>(initialState: T): [T, Dispatch<T>, React.MutableRefObject<T>] {
  const [state, setState] = useState<T>(initialState);
  const stateRef = useRef<T>(state);
  useEffect(() => {
    if (!isEqual(state, stateRef.current)) {
      stateRef.current = state;
    }
  }, [state]);

  return [state, setState, stateRef];
}
const CoverageRequirements = (props: ICoverageRequirementsProps) => {
  const dispatch = useAppDispatch()
  const isReadOnlyStudy: boolean | undefined = props?.isReadOnlyStudy
  const [datasetPath, setDatasetPath, refDatasetPath] = useDeepState<string[]>([])

  useEffect(() => {
    return () => { //TOD0: Runs still updating redux after closing global dialog?
      dispatch(clearCoverageRequirementsState())
    }
  }, []);

  const fetchHandleModifyNode = usePostRequest(coverageRequirementsModifyTreeEndpoint)
  const onClickAbort = useCallback((datasetId: string) => {
    let payload: any = {
      "executionStatus":  EDatasetExecutionStatus.ABORTED,
    }
    /**
     * On the frontend, when we click generate, we store a map with datasetIds to abort controllers. Check onClickExecute
     * in tree component.
     *
     * Let's say we just want to do backend first. We need to send a request to the backend to abort the analysis.
     * We modify the redline dataset to have a status of "ABORTED". We don't touch the tree or anything else. The tree
     * polls the redline datasets.
     */
    fetchHandleModifyNode({
      "nodeData": payload,
      "action": "edit",
      "datasetId": datasetId,
      "nodeType": "dataset",
      studyId: props.studyId
    }).then((res) => {
      // console.log(res)
    }).catch((err) => {
      console.log(err)
    })
  }, [])
  const [seed, setSeed] = useState(1);
  const isGlobalDialogOpen = useIsGlobalDialogOpen()

  return (
    <ArgosExpandableContainer
      key={`CoverageRequirementsItemDetails-dialogOpen=${isGlobalDialogOpen}`}
      style={{width: '100%', overflow: "hidden", height: "100%"}}
      initialLeftSize={'25%'}
      leftSide={
      <LeftPanelCoverageRequirements
        studyId={props.studyId}
        studyName={props.studyName}
        isPlanOfRecord={props.isPlanOfRecord}
        setDatasetPath={setDatasetPath}
        setDatasetChange={props.setDatasetChange}
        onClickAbort={onClickAbort}
        datasetChange={props.datasetChange}
        setSeed={setSeed}
        fetchStudy={props.fetchStudy}
        isReadOnlyStudy={isReadOnlyStudy}
      />}
      rightSide={
      <RightPanelCoverageRequirements
        key={seed+"RightPanelCoverageRequirements"}
        studyId={props.studyId}
        // datasetPath={refDatasetPath.current}
        fetchStudy={props.fetchStudy}
        datasetPath={datasetPath}
        datasetChange={props.datasetChange}
        setDatasetChange={props.setDatasetChange}
        onClickAbort={onClickAbort}
        isReadOnlyStudy={isReadOnlyStudy}
      />}/>
  )
}

export const getCoverageRequirementsSelectedDatasetId = () => {
  const queryParameters = new URLSearchParams(window.location.search)
  const datasetId: any = queryParameters.get(SELECTED_DATASET_ID_URL_PARAM)
  return datasetId
}

export default CoverageRequirements
